(() => {
  const RESERVATION_ENABLED = false;
  const STORAGE_KEY = 'needohCartReservationExpiresAtV1';
  if (!RESERVATION_ENABLED) {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Browser storage may be unavailable in privacy modes.
    }
    return;
  }

  const ELEMENT_NAME = 'needoh-cart-reservation';
  if (customElements.get(ELEMENT_NAME)) return;

  const DURATION = 20 * 60 * 1000;
  const RETRY_DELAY = 30 * 1000;
  let memoryDeadline = 0;
  let clearing = null;
  let retryAfter = 0;

  const readDeadline = () => {
    try {
      const value = Number(localStorage.getItem(STORAGE_KEY));
      return Number.isFinite(value) && value > 0 ? value : memoryDeadline;
    } catch {
      return memoryDeadline;
    }
  };

  const writeDeadline = (value) => {
    memoryDeadline = value;
    try {
      if (value) localStorage.setItem(STORAGE_KEY, String(value));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Keep the timer working in privacy modes that disable browser storage.
    }
  };

  class NeedohCartReservation extends HTMLElement {
    connectedCallback() {
      this.cartUrl = this.dataset.cartUrl || '/cart';
      this.timeElement = this.querySelector('[data-reservation-time]');
      this.onVisibility = () => {
        if (document.visibilityState === 'visible') {
          this.tick();
          this.refreshCart();
        }
      };
      this.onPageShow = () => {
        this.tick();
        this.refreshCart();
      };
      this.onStorage = (event) => {
        if (event.key === STORAGE_KEY || event.key === null) {
          memoryDeadline = Number(event.newValue) || 0;
          this.refreshCart();
        }
      };
      this.onCheckout = (event) => {
        const checkout = event.type === 'submit'
          ? event.submitter?.name === 'checkout'
          : event.target.closest?.('a[href*="/checkout"], button[name="checkout"]');
        if (checkout && !this.hidden && readDeadline() <= Date.now()) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.expire();
        }
      };

      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('click', this.onCheckout, true);
      document.addEventListener('submit', this.onCheckout, true);
      window.addEventListener('pageshow', this.onPageShow);
      window.addEventListener('storage', this.onStorage);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (Number.isFinite(event.cartData?.item_count)) {
            this.sync(event.cartData.item_count);
          } else {
            return this.refreshCart();
          }
        });
      }

      this.sync(Number(this.dataset.cartCount) || 0);
      this.interval = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.interval);
      this.unsubscribe?.();
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('click', this.onCheckout, true);
      document.removeEventListener('submit', this.onCheckout, true);
      window.removeEventListener('pageshow', this.onPageShow);
      window.removeEventListener('storage', this.onStorage);
    }

    sync(itemCount) {
      this.hidden = itemCount === 0;
      if (this.hidden) {
        writeDeadline(0);
        retryAfter = 0;
        return;
      }

      const deadline = readDeadline();
      if (!deadline || deadline > Date.now() + DURATION) {
        writeDeadline(Date.now() + DURATION);
      }
      this.tick();
    }

    tick() {
      if (this.hidden || !this.timeElement) return;
      const remaining = Math.max(0, Math.ceil((readDeadline() - Date.now()) / 1000));
      const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
      const seconds = String(remaining % 60).padStart(2, '0');
      this.timeElement.textContent = `${minutes}:${seconds}`;
      if (remaining === 0) this.expire();
    }

    async refreshCart() {
      if (this.refreshing || clearing) return;
      this.refreshing = true;
      try {
        const response = await fetch(`${this.cartUrl}.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error('Unable to read cart');
        const cart = await response.json();
        if (this.isConnected) this.sync(cart.item_count);
      } catch {
        // Do not reset the reservation when a cart refresh fails.
      } finally {
        this.refreshing = false;
      }
    }

    expire() {
      if (clearing || Date.now() < retryAfter) return clearing;
      clearing = this.clearCart()
        .catch(() => {
          retryAfter = Date.now() + RETRY_DELAY;
          const status = document.getElementById('CartDrawer-LiveRegionText');
          if (status) {
            status.removeAttribute('aria-hidden');
            status.textContent = 'We could not refresh your cart. Please try again.';
          }
        })
        .finally(() => { clearing = null; });
      return clearing;
    }

    async clearCart() {
      const drawer = document.querySelector('cart-drawer');
      const mainItems = document.getElementById('main-cart-items');
      const mainFooter = document.getElementById('main-cart-footer');
      const sections = [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
      if (mainItems) sections.push({ id: mainItems.id, section: mainItems.dataset.id, selector: '.js-contents' });
      if (mainFooter) sections.push({ id: mainFooter.id, section: mainFooter.dataset.id, selector: '.js-contents' });

      const response = await fetch(`${this.cartUrl}/clear.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          sections: [...new Set(sections.map((section) => section.section).filter(Boolean))],
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error('Unable to clear cart');
      const cart = await response.json();
      if (cart.item_count !== 0) throw new Error('Cart was not cleared');
      writeDeadline(0);
      retryAfter = 0;

      // Replace the same sections as Dawn without opening a closed drawer.
      let missingSection = false;
      sections.forEach((section) => {
        const target = document.getElementById(section.id);
        if (!target) return;
        const html = cart.sections?.[section.section];
        const source = html && new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
        if (!source) {
          missingSection = true;
          return;
        }
        const contents = section.id === 'CartDrawer' || section.id === 'cart-icon-bubble'
          ? target
          : target.querySelector(section.selector);
        if (contents) contents.innerHTML = source.innerHTML;
        else missingSection = true;
      });
      drawer?.classList.add('is-empty');
      mainItems?.classList.add('is-empty');
      mainFooter?.classList.add('is-empty');
      if (missingSection) {
        window.location.reload();
        return;
      }

      drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      if (drawer?.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(drawer.querySelector('.drawer__inner-empty'), drawer.querySelector('.cart__empty-content .button'));
      }
      document.querySelectorAll(ELEMENT_NAME).forEach((element) => element.sync(0));
      const status = document.getElementById('CartDrawer-LiveRegionText');
      if (status) {
        status.removeAttribute('aria-hidden');
        status.textContent = 'Your reservation has expired. Your cart is now empty.';
      }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'needoh-cart-reservation', cartData: cart });
      }
    }
  }

  customElements.define(ELEMENT_NAME, NeedohCartReservation);
})();
